import { useEffect } from 'react';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { AuthenticationService } from '@src/api/web-services/AuthenticationService';
import { AlertService } from '@src/api/common/AlertService';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { ApiStatus } from '@src/constants/ApiStatus';
import { CookieService } from '@src/api/common/CookieService';
import { UserService } from '@src/api/common/UserService';
import { JwtService } from '@src/api/common/JwtService';
import { useAuthSessionStore } from '@src/store/common/AuthSessionStore';

const SingleSignOnController = () => {
  const { SSOGetAccessToken, SSOInitiateUrl, CallSSOEndPoint } =
    AuthenticationService();
  const { navigate } = useRouter();
  const { AlertModel, ShowAlert, HideAlert } = AlertService();

  const clearAuthState = () => {
    localStorage.removeItem('isLoggedIn');
    UserService.ClearAuthenticationCookies();
    useAuthSessionStore.getState().syncFromCookies({ incrementEpoch: true });
  };

  const isValidAuthentication = (): boolean => {
    const token = UserService.GetToken();
    const user = UserService.GetUser();
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

    // All three must be present for valid authentication
    if (!token || !user || !isLoggedIn) {
      return false;
    }

    // Validate token expiry
    if (JwtService.IsTokenExpired(token)) {
      return false;
    }

    return true;
  };

  const handleSSOError = (apiData: any, errorContext: string) => {
    const hasApiError = apiData.data?.hasError === true;
    const hasHttpError =
      apiData.status &&
      apiData.status !== 200 &&
      apiData.status !== ApiStatus.Success;

    if (hasApiError || hasHttpError) {
      let errorMessage = 'Unknown SSO error occurred';

      if (apiData.data?.message) {
        errorMessage = apiData.data.message;
      } else if (apiData.message) {
        errorMessage = apiData.message;
      }

      console.error(`SSO ${errorContext} failed:`, {
        hasError: apiData.data?.hasError,
        httpStatus: apiData.status,
        message: errorMessage,
      });

      const encodedError = encodeURIComponent(errorMessage);
      navigate(`/not-authenticated?error=${encodedError}`);
      return true; // Indicates error was handled
    }

    return false;
  };

  const Datasource = {
    InitiateSSOUrl: async () => {
      const apiData = await SSOInitiateUrl();

      // Check for SSO errors first
      if (handleSSOError(apiData, 'initiate URL')) {
        return;
      }

      // Success path
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        let generatedUrl = apiData.data?.result?.url;
        window.location.href = generatedUrl;
      }
    },
    SSOGetAccessToken: async (code: string | null) => {
      const payload = { code: code };
      const apiData = await SSOGetAccessToken(payload);

      // Check for SSO errors first
      if (handleSSOError(apiData, 'get access token')) {
        return;
      }

      // Success path
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        let accessToken = apiData?.data.result.tokenResult?.token;
        await Datasource.CallSSOEndPoint(accessToken);
      }
    },
    CallSSOEndPoint: async (accessToken: string | null) => {
      const payload = { token: accessToken };
      const apiData = await CallSSOEndPoint(payload);

      // Check for SSO errors first
      if (handleSSOError(apiData, 'SSO endpoint')) {
        return;
      }

      // Success path
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        var cookieOptions = { path: '/', secure: false };
        CookieService.SaveCookie(
          'authToken',
          apiData.data?.result?.accessToken,
          cookieOptions
        );
        CookieService.SaveCookie(
          'authUser',
          JSON.stringify(apiData.data?.result?.user),
          cookieOptions
        );
        localStorage.setItem('isLoggedIn', 'true');

        window.location.href = '/responses';
      }
    },
  };

  const Action = {
    InitiateSSO: async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        await Datasource.SSOGetAccessToken(code);
      } else {
        await Datasource.InitiateSSOUrl();
      }
    },
  };

  useEffect(() => {
    // Check if user is already properly authenticated
    if (isValidAuthentication()) {
      navigate('/responses');
      return;
    }

    clearAuthState();

    // Start SSO flow
    Action.InitiateSSO();
  }, []);

  return { Action };
};

export { SingleSignOnController };
